import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import React, { useMemo, useState } from "react";
import { Modal, Menu, Tag, Alert, Card, Button, Row, Col, Typography, Spin, Steps } from "antd";
import { CheckCircleTwoTone, ExclamationCircleOutlined } from "@ant-design/icons";
import ShopType from "@/enum/ShopType";
import { useGlobalStore } from "@/store/hooks";
import { useRequest } from "ahooks";
import EwaybillType from "@/enum/EwaybillType";
import {
    AddPddShopTutorial,
    AddShopTutorial,
    AddWaybillTemplateTutorial,
    AlibabaEwaybillUrl,
    authorizeChannelShopDropshipEwaybillTutorial,
    authorizeJdEwaybillTutorial,
    authorizeJinritemaiAuthEwaybillTutorial,
    authorizeJinritemaiEwaybillTutorial,
    authorizeKwaiAuthEwaybillTutorial,
    authorizeKwaiEwaybillTutorial,
    authorizePddAuthEwaybillTutorial,
    authorizePddEwaybillTutorial,
    authorizeTbEwaybillTutorial,
    authorizeXhsAuthEwaybillTutorial,
    authorizeXhsEwaybillTutorial,
    openJdEwaybillTutorial,
    openJdEwaybillUrl,
    openChannelShopDropshipEwaybillTutorial,
    openChannelShopDropshipEwaybillUrl,
    openJinritemaiAuthEwaybillTutorial,
    openJinritemaiAuthEwaybillUrl,
    openJinritemaiEwaybillTutorial,
    openJinritemaiEwaybillUrl,
    openKwaiAuthEwaybillTutorial,
    openKwaiAuthEwaybillUrl,
    openKwaiEwaybillTutorial,
    openKwaiEwaybillUrl,
    openPddAuthEwaybillTutorial,
    openPddAuthEwaybillUrl,
    openPddEwaybillTutorial,
    openPddEwaybillUrl,
    openTbEwaybillTutorial,
    openTbEwaybillUrl,
    openXhsAuthEwaybillTutorial,
    openXhsAuthEwaybillUrl,
    openXhsEwaybillTutorial,
    openXhsEwaybillUrl,
} from "@/utils/Url";
import { openUrl } from "@/utils";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import { uploadEwaybillOpenStatus } from "@/services/api/alibaba/encryptedOutOrder";
import EwaybillAccountDTO from "@/dto/ewaybillAccount/EwaybillAccountDTO";
import { buildEwaybillAccountInfo } from "@/utils/EwaybillUtil";
import EwaybillTypeNameMap from "@/map/EwaybillTypeNameMap";
import WaybillTemplateType from "@/enum/WaybillTemplateType";
import { observer } from "mobx-react-lite";
import { MenuDivider, MenuItem, MenuItemGroup, SubMenu, buildMenuItems } from "@/utils/antdItems";

interface EwaybillAuthModalProps extends InjectBaseModalProps {
    shopId: number;
}

interface InactiveSection {
    title: string;
    desc: string[];
    tutorial: string;
    open: string;
}

interface ActivatedSection {
    authorizeShopUrl?: string;
    authorizeShopAuthUrl?: string;
}

interface PlatformConfig {
    key: ShopType;
    label: string;
    supportedTypes: EwaybillType[];
    /** 已开通 */
    activatedSection: ActivatedSection;
    /* 未开通 */
    inactiveSections: InactiveSection[];
}

const configs: Record<string, PlatformConfig> = {
    [ShopType.DOUDIAN]: {
        key: ShopType.DOUDIAN,
        label: "抖店",
        supportedTypes: [
            EwaybillType.JINRITEMAI,
            EwaybillType.JINRITEMAI_AUTH,
            EwaybillType.JINRITEMAI_AUTH_SHARE,
            EwaybillType.JINRITEMAI_SHARE,
            EwaybillType.JINRITEMAI_SHOP_AUTH,
            EwaybillType.JINRITEMAI_SUPPLIER,
        ],
        inactiveSections: [
            {
                title: "开通抖店站外电子面单",
                desc: ["无抖店的商家适用", "通过ebill.douyinec.com注册后开通网点", "且需订购发货易应用"],
                tutorial: openJinritemaiAuthEwaybillTutorial,
                open: openJinritemaiAuthEwaybillUrl,
            },
            {
                title: "开通抖店店铺电子面单",
                desc: ["有抖店的商家适用", "需在店铺后台开通网点", "且需订购发货易应用"],
                tutorial: openJinritemaiEwaybillTutorial,
                open: openJinritemaiEwaybillUrl,
            },
        ],
        activatedSection: {
            authorizeShopUrl: authorizeJinritemaiEwaybillTutorial,
            authorizeShopAuthUrl: authorizeJinritemaiAuthEwaybillTutorial,
        },
    },
    [ShopType.PINDUODUO]: {
        key: ShopType.PINDUODUO,
        label: "拼多多",
        supportedTypes: [EwaybillType.PDD, EwaybillType.PDD_SHARE, EwaybillType.PDD_AUTH, EwaybillType.PDD_AUTH_SHARE],
        inactiveSections: [
            {
                title: "开通拼多多站外电子面单",
                desc: ["无拼多多店铺的商家适用", "通过https://wb.pinduoduo.com/注册后开通网点", "且需订购发货易应用"],
                tutorial: openPddAuthEwaybillTutorial,
                open: openPddAuthEwaybillUrl,
            },
            {
                title: "开通拼多多店铺电子面单",
                desc: ["有拼多多店铺的商家适用", "需在店铺后台开通网点", "且需订购发货易应用"],
                tutorial: openPddEwaybillTutorial,
                open: openPddEwaybillUrl,
            },
        ],
        activatedSection: {
            authorizeShopUrl: authorizePddEwaybillTutorial,
            authorizeShopAuthUrl: authorizePddAuthEwaybillTutorial,
        },
    },
    [ShopType.KWAI]: {
        key: ShopType.KWAI,
        label: "快手",
        supportedTypes: [EwaybillType.KWAISHOP, EwaybillType.KWAISHOP_SHARE, EwaybillType.KWAISHOP_SHOP_AUTH],
        inactiveSections: [
            {
                title: "开通快手站外电子面单",
                desc: ["无快手店铺的商家适用", "通过快手官方打单注册后开通网点", "且需订购发货易应用"],
                tutorial: openKwaiAuthEwaybillTutorial,
                open: openKwaiAuthEwaybillUrl,
            },
            {
                title: "开通快手店铺电子面单",
                desc: ["有快手店铺的商家适用", "需在店铺后台开通网点", "且需订购发货易应用"],
                tutorial: openKwaiEwaybillTutorial,
                open: openKwaiEwaybillUrl,
            },
        ],
        activatedSection: {
            authorizeShopUrl: authorizeKwaiEwaybillTutorial,
            authorizeShopAuthUrl: authorizeKwaiAuthEwaybillTutorial,
        },
    },
    [ShopType.TAOBAO]: {
        key: ShopType.TAOBAO,
        label: "淘宝菜鸟",
        supportedTypes: [
            EwaybillType.CAINIAO,
            EwaybillType.TAOBAO_CAINIAO,
            EwaybillType.TAOBAO_CAINIAO_AUTH,
            EwaybillType.TAOBAO_CAINIAO_AUTH_SHARE,
        ],
        inactiveSections: [
            {
                title: "开通淘宝菜鸟电子面单",
                desc: ["有淘宝账号的商家适用", "通过淘宝菜鸟官网注册后开通", "且需订购发货易应用"],
                tutorial: openTbEwaybillTutorial,
                open: openTbEwaybillUrl,
            },
        ],
        activatedSection: {
            authorizeShopUrl: authorizeTbEwaybillTutorial,
        },
    },
    [ShopType.JD]: {
        key: ShopType.JD,
        label: "京东无界",
        supportedTypes: [EwaybillType.JD, EwaybillType.JD_SHARE, EwaybillType.JD_AUTH, EwaybillType.JDL],
        inactiveSections: [
            {
                title: "开通京东无界电子面单",
                desc: ["有京东店铺的商家适用", "需在店铺后台开通网点", "且需订购发货易应用"],
                tutorial: openJdEwaybillTutorial,
                open: openJdEwaybillUrl,
            },
        ],
        activatedSection: {
            authorizeShopUrl: authorizeJdEwaybillTutorial,
        },
    },
    [ShopType.XIAOHONGSHU]: {
        key: ShopType.XIAOHONGSHU,
        label: "小红书",
        supportedTypes: [EwaybillType.XIAOHONGSHU, EwaybillType.XIAOHONGSHU_AUTH, EwaybillType.XIAOHONGSHU_SHARE],
        inactiveSections: [
            {
                title: "开通小红书站外电子面单",
                desc: [
                    "无小红书店铺的商家适用",
                    "通过https://ebill.xiaohongshu.com/注册后开通网点",
                    "且需订购发货易应用",
                ],
                tutorial: openXhsAuthEwaybillTutorial,
                open: openXhsAuthEwaybillUrl,
            },
            {
                title: "开通小红书店铺电子面单",
                desc: ["有小红书店铺的商家适用", "需在店铺后台开通网点", "且需订购发货易应用"],
                tutorial: openXhsEwaybillTutorial,
                open: openXhsEwaybillUrl,
            },
        ],
        activatedSection: {
            authorizeShopUrl: authorizeXhsEwaybillTutorial,
            authorizeShopAuthUrl: authorizeXhsAuthEwaybillTutorial,
        },
    },
    [ShopType.CHANNEL_SHOP_DROPSHIP]: {
        key: ShopType.CHANNEL_SHOP_DROPSHIP,
        label: "微信小店供货商代发",
        supportedTypes: [EwaybillType.CHANNEL_SHOP_DROPSHIP],
        inactiveSections: [
            {
                title: "开通微信小店供货商代发电子面单",
                desc: [
                    "有微信小店供货商代发的商家适用",
                    "通过微信小店供货商代发官网注册后开通",
                    "且需订购发货易供货商应用",
                ],
                tutorial: openChannelShopDropshipEwaybillTutorial,
                open: openChannelShopDropshipEwaybillUrl,
            },
        ],
        activatedSection: {
            authorizeShopUrl: authorizeChannelShopDropshipEwaybillTutorial,
        },
    },
    [ShopType.CHANNEL_SHOP_SUPPLIER]: {
        key: ShopType.CHANNEL_SHOP_SUPPLIER,
        label: "微信小店代发",
        supportedTypes: [EwaybillType.CHANNEL_SHOP_DROPSHIP],
        inactiveSections: [
            {
                title: "开通微信小店供货商电子面单",
                desc: ["有微信小店供货商的商家适用", "通过微信小店供货商官网注册后开通", "且需订购发货易供货商应用"],
                tutorial: openChannelShopDropshipEwaybillTutorial,
                open: openChannelShopDropshipEwaybillUrl,
            },
        ],
        activatedSection: {
            authorizeShopUrl: authorizeChannelShopDropshipEwaybillTutorial,
        },
    },
};

const { Title, Paragraph } = Typography;

const EwaybillAuthModal: React.FC<EwaybillAuthModalProps> = function (props) {
    const { open, onCancel, shopId } = props;
    const [selectedKey, setSelectedKey] = useState(ShopType.DOUDIAN);
    const globalStore = useGlobalStore();

    const { loading: loading1 } = useRequest(globalStore.ewaybillAccountStore.ensure);
    const { loading: loading2 } = useRequest(globalStore.waybillPrintStore.ensureWaybillTemplates);

    const loading = loading1 || loading2;

    const menuItems = useMemo(() => {
        const platformEwaybillInfoMap = new Map<
            ShopType,
            { ewaybillAccounts: EwaybillAccountDTO[]; hasTemplate: boolean }
        >();

        Object.values(configs).forEach((config) => {
            platformEwaybillInfoMap.set(config.key, { ewaybillAccounts: [], hasTemplate: false });
        });

        // 填充账户信息
        globalStore.ewaybillAccountStore.rawEwaybillAccounts.forEach((ewaybillAccount) => {
            Object.entries(configs).forEach(([key, config]) => {
                if (config.supportedTypes.includes(ewaybillAccount.type)) {
                    const info = platformEwaybillInfoMap.get(config.key)!;
                    info.ewaybillAccounts.push(ewaybillAccount);
                }
            });
        });

        // 填充模板信息
        globalStore.waybillPrintStore.waybillTemplates.forEach((template) => {
            switch (template.type) {
                case WaybillTemplateType.JINRITEMAI:
                    platformEwaybillInfoMap.get(ShopType.DOUDIAN).hasTemplate = true;
                    break;

                case WaybillTemplateType.PDD:
                    platformEwaybillInfoMap.get(ShopType.PINDUODUO).hasTemplate = true;
                    break;
                case WaybillTemplateType.KWAISHOP:
                    platformEwaybillInfoMap.get(ShopType.KWAI).hasTemplate = true;
                    break;

                case WaybillTemplateType.CAINIAO:
                case WaybillTemplateType.TAOBAO_CAINIAO:
                    platformEwaybillInfoMap.get(ShopType.TAOBAO).hasTemplate = true;
                    break;
                case WaybillTemplateType.JD:
                case WaybillTemplateType.JDL:
                    platformEwaybillInfoMap.get(ShopType.JD).hasTemplate = true;
                    break;
                case WaybillTemplateType.XIAOHONGSHU:
                    platformEwaybillInfoMap.get(ShopType.XIAOHONGSHU).hasTemplate = true;
                    break;
                case WaybillTemplateType.CHANNEL_SHOP:
                    platformEwaybillInfoMap.get(ShopType.CHANNEL_SHOP_DROPSHIP).hasTemplate = true;
                    platformEwaybillInfoMap.get(ShopType.CHANNEL_SHOP_SUPPLIER).hasTemplate = true;
                    break;
                default:
                    break;
            }
        });

        const resultMenuItems = Object.values(configs).map((config) => {
            const defaultInfo = platformEwaybillInfoMap.get(config.key);
            const status = defaultInfo?.ewaybillAccounts.length > 0;
            const hasTemplate = defaultInfo?.hasTemplate || false;

            return {
                key: config.key,
                config,
                label: `${config.label}电子面单`,
                status,
                ewaybillAccounts: defaultInfo?.ewaybillAccounts || [],
                hasTemplate,
            };
        });

        return resultMenuItems;
    }, [
        globalStore.ewaybillAccountStore.rawEwaybillAccounts,
        globalStore.waybillPrintStore.waybillTemplates,
        globalStore.ewaybillAccountStore.ewaybillAccountId2AccountMapping,
    ]);
    const item = menuItems.find((i) => i.key === selectedKey);

    const renderContent = () => {
        if (!item || loading) return null;
        const { status, ewaybillAccounts, config, hasTemplate } = item;

        const CommonAlert = (
            <Alert
                title={<span className="fs12">为什么开通分销平台的电子面单？</span>}
                description={
                    <span className="fs12">
                        覆盖抖音、快手、拼多多、小红书、京东、微信小店等1688平台密文分销订单 —— 这类订单
                        需使用对应平台的电子面单，才能完成打单发货流程
                    </span>
                }
                showIcon
                type="warning"
                style={{ marginBottom: 24 }}
            />
        );

        return (
            <div>
                {CommonAlert}
                {!status ? (
                    <>
                        <Title level={5} className="mt30">
                            <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
                            <span className="ml5">未开通{config.label}电子面单，请选择以下开通方式</span>
                        </Title>

                        <Row gutter={16}>
                            {config.inactiveSections.map((section) => {
                                const onlyOne = config.inactiveSections.length === 1;

                                return (
                                    <Col span={onlyOne ? 24 : 24 / config.inactiveSections.length} key={section.title}>
                                        <Card title={<div className="tc fb">{section.title}</div>} className="tc">
                                            <Paragraph>
                                                {section.desc.map((des, index) => {
                                                    return (
                                                        <React.Fragment key={index}>
                                                            <span className="text-gray">{des}</span>
                                                            <br />
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </Paragraph>
                                            <a href={section.tutorial} target="_blank" rel="noreferrer">
                                                查看开通教程
                                            </a>
                                            <div style={{ marginTop: 8 }}>
                                                <Button
                                                    type="primary"
                                                    onClick={() => {
                                                        openUrl(section.open);
                                                    }}
                                                >
                                                    去开通
                                                </Button>
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>

                        <Title level={5} className="mt30">
                            已开通{config.label}电子面单，请选择一种方式授权
                        </Title>

                        <Card>
                            {config.activatedSection.authorizeShopAuthUrl ? (
                                <div className="mb10">
                                    已有{config.label}站外电子面单
                                    <Button
                                        type="primary"
                                        className="ml10"
                                        onClick={() => {
                                            openUrl("https://v2.fahuoyi.com/setting/ewaybillAccount");
                                        }}
                                    >
                                        授权电子面单使用
                                    </Button>
                                    <a
                                        href={config.activatedSection.authorizeShopAuthUrl}
                                        className="ml5"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        查看授权教程
                                    </a>
                                </div>
                            ) : null}

                            {config.activatedSection.authorizeShopUrl ? (
                                <div>
                                    已有{config.label}店铺电子面单
                                    <Button
                                        className="ml10"
                                        type="primary"
                                        onClick={() => {
                                            openUrl("https://v2.fahuoyi.com/setting/shop/add");
                                        }}
                                    >
                                        关联店铺使用
                                    </Button>
                                    <Button
                                        type="primary"
                                        className="ml10"
                                        onClick={() => {
                                            openUrl("https://v2.fahuoyi.com/setting/ewaybillAccount");
                                        }}
                                    >
                                        授权电子面单使用
                                    </Button>
                                    <a
                                        href={config.key !== ShopType.PINDUODUO ? AddShopTutorial : AddPddShopTutorial}
                                        className="ml5"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        查看关联教程
                                    </a>
                                    ｜
                                    <a
                                        href={config.activatedSection.authorizeShopUrl}
                                        className="ml5"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        查看授权教程
                                    </a>
                                </div>
                            ) : null}
                        </Card>
                    </>
                ) : (
                    <>
                        <Steps
                            orientation="vertical"
                            current={1}
                            items={[
                                {
                                    title: (
                                        <div>
                                            <span>已开通{config.label}电子面单</span>
                                            {!hasTemplate ? (
                                                <span className="fs13">
                                                    <span className="ml10 fb error">未添加快递单模板</span>
                                                    <a
                                                        href={AddWaybillTemplateTutorial}
                                                        target="_blank"
                                                        className="ml10"
                                                    >
                                                        查看添加模板教程
                                                    </a>
                                                </span>
                                            ) : null}
                                        </div>
                                    ),
                                    content: ewaybillAccounts?.map((ewaybillAccount, index) => {
                                        const { id, type } = ewaybillAccount;
                                        const accountInfo = buildEwaybillAccountInfo(
                                            ewaybillAccount,
                                            globalStore.shopMapping,
                                        );

                                        return (
                                            <p key={id} className="mr5">
                                                {index + 1}.{accountInfo.name}({EwaybillTypeNameMap[type]})
                                            </p>
                                        );
                                    }),
                                    icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
                                },
                                {
                                    title: "去1688商家后台查看认证结果",
                                    content: (
                                        <span style={{ color: "red" }}>
                                            密文认证状态以阿里商家后台显示的状态为准，如已认证通过可忽略该步骤
                                        </span>
                                    ),
                                    icon: <ExclamationCircleOutlined style={{ color: "red" }} />,
                                },
                            ]}
                        />
                        <AutoLoadingButton
                            type="primary"
                            style={{ marginLeft: 50 }}
                            onClick={async () => {
                                await uploadEwaybillOpenStatus(shopId);
                                openUrl(AlibabaEwaybillUrl);
                            }}
                        >
                            去查看
                        </AutoLoadingButton>
                    </>
                )}
            </div>
        );
    };

    return (
        <Modal
            title="1688密文面单认证"
            width={1140}
            open={open}
            onCancel={onCancel}
            footer={null}
            styles={{ body: { display: "flex", padding: 0 } }}
        >
            <Menu
                mode="inline"
                selectedKeys={[selectedKey]}
                style={{ width: 280, borderRight: 0 }}
                onClick={({ key }) => setSelectedKey(key as ShopType)}
                items={buildMenuItems(
                    <>
                        {menuItems.map((i) => (
                            <MenuItem key={i.key}>
                                <span className="flex justify-between flex-center">
                                    <span>{i.label}</span>
                                    {!loading ? (
                                        <Tag
                                            variant="outlined"
                                            color={i.status ? "blue" : "orange"}
                                            style={{ marginLeft: 8 }}
                                        >
                                            {i.status ? "已开通" : "未开通"}
                                        </Tag>
                                    ) : null}
                                </span>
                            </MenuItem>
                        ))}
                    </>,
                )}
            />

            <Spin spinning={loading}>
                <div style={{ padding: "3px 24px", flex: 1, overflowY: "auto", minHeight: 600 }}>{renderContent()}</div>
            </Spin>
        </Modal>
    );
};

export default observer(EwaybillAuthModal);
